import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import whiteIcon from '../../assets/images/whiteIcon.png';
import whiteLogo from '../../assets/images/whiteLogo.png';
import { AuthContext } from '../../contexts/AuthContext';
import JumpToAccountModal from '../../modals/JumpToAccountModal';
import { classNames } from '../../utils';

export type DashboardSidebarCollapseMode = 'open' | 'open-on-hover';

export interface SidebarItem {
  name: string;
  href: string;
  icon: any;
  current: boolean;
  count?: number;
}

export interface SidebarTeamItem {
  id: string;
  name: string;
  current: boolean;
  initial: string;
  href: string;
}

interface Props {
  navigation: SidebarItem[];
  teams: SidebarTeamItem[];
  collapsed?: boolean;
  collapseMode: DashboardSidebarCollapseMode;
  onCollapseModeChange: (mode: DashboardSidebarCollapseMode) => void;
}

export default function DashboardSidebar({
  navigation,
  teams,
  collapsed,
}: Props) {
  const navigate = useNavigate();
  const { firebaseUser, isSupportAdmin } = useContext(AuthContext);
  const [showAllTeams, setShowAllTeams] = useState(false);
  const [showJumpToModal, setShowJumpToModal] = useState(false);

  return (
    <>
      {isSupportAdmin ? (
        <JumpToAccountModal
          open={showJumpToModal}
          onClose={() => setShowJumpToModal(false)}
          onSelect={(account) => {
            navigate(`/workspace/${account.data().slug}`);
            setShowJumpToModal(false);
          }}
        />
      ) : (
        <></>
      )}
      <div
        className={classNames(
          'flex grow flex-col gap-y-5 overflow-y-auto overflow-x-hidden bg-indigo-600 transition-all duration-300',
          collapsed ? 'px-5' : 'px-6',
        )}
      >
        <div
          className={classNames(
            'flex h-16 shrink-0 items-center',
            collapsed ? 'justify-center' : 'justify-between',
          )}
        >
          <img
            className={classNames(
              'mt-5 h-6 w-auto cursor-pointer object-cover object-left',
              collapsed ? 'hidden' : 'block',
            )}
            src={whiteLogo}
            alt="BetterFunnel"
            onClick={() => navigate('/workspaces')}
          />
          <img
            className={classNames(
              'mt-5 h-6 w-auto cursor-pointer',
              collapsed ? 'block' : 'hidden',
            )}
            src={whiteIcon}
            alt="BetterFunnel"
            onClick={() => navigate('/workspaces')}
          />
        </div>
        <nav className="flex flex-1 flex-col">
          <ul role="list" className="flex flex-1 flex-col gap-y-7">
            <li>
              <ul role="list" className="-mx-2 space-y-1">
                {navigation.map((item) => (
                  <li key={item.name} className="active:outline-none">
                    {/* <Tooltip text={item.name} position={'right'}> */}
                    <button
                      onClick={() => navigate(item.href)}
                      className={classNames(
                        item.current
                          ? 'bg-indigo-700 text-white'
                          : 'text-indigo-200 hover:bg-indigo-700 hover:text-white',
                        'group flex w-full gap-x-3 whitespace-nowrap rounded-md p-2 text-sm font-semibold leading-6',
                      )}
                    >
                      <item.icon
                        className={classNames(
                          item.current
                            ? 'text-white'
                            : 'text-indigo-200 group-hover:text-white',
                          'h-6 w-6 shrink-0',
                        )}
                        aria-hidden="true"
                      />
                      <span
                        className={classNames(
                          'transition-opacity',
                          collapsed ? 'opacity-0' : 'opacity-100',
                        )}
                      >
                        {item.name}
                      </span>
                      {item.count ? (
                        <span
                          className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-indigo-600 px-2.5 py-0.5 text-center text-xs font-medium leading-5 text-white ring-1 ring-inset ring-indigo-500"
                          aria-hidden="true"
                        >
                          {item.count}
                        </span>
                      ) : null}
                    </button>
                    {/* </Tooltip> */}
                  </li>
                ))}
              </ul>
            </li>
            <li>
              <div
                className={classNames(
                  'whitespace-nowrap text-xs font-semibold leading-6 text-indigo-200',
                  collapsed ? 'invisible' : 'visible',
                )}
              >
                Your workspaces
              </div>
              <ul role="list" className="-mx-2 mt-2 space-y-1">
                {(showAllTeams ? teams : teams.slice(0, 3)).map((team) => (
                  <li key={team.id || team.name}>
                    <button
                      className={classNames(
                        team.current
                          ? 'bg-indigo-700 text-white'
                          : 'text-indigo-200 hover:bg-indigo-700 hover:text-white',
                        'group flex w-full gap-x-3 rounded-md p-2 text-sm font-semibold leading-6',
                      )}
                      onClick={() => {
                        navigate(team.href);
                      }}
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-indigo-400 bg-indigo-500 text-[0.625rem] font-medium text-white">
                        {team.initial}
                      </span>
                      <span
                        className={classNames(
                          'truncate transition-opacity',
                          collapsed ? 'opacity-0' : 'opacity-100',
                        )}
                      >
                        {team.name}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {isSupportAdmin ? (
                <div className="-mx-2 mt-1 space-y-1 whitespace-nowrap">
                  <button
                    className={classNames(
                      'text-indigo-200 hover:bg-indigo-700 hover:text-white',
                      'group flex w-full gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 transition-opacity',
                      collapsed ? 'opacity-0' : 'opacity-100',
                    )}
                    onClick={() => {
                      setShowJumpToModal(true);
                    }}
                  >
                    <span className="truncate">Jump to...</span>
                  </button>
                </div>
              ) : (
                <></>
              )}
              {!showAllTeams && (teams?.length || 0) > 3 ? (
                <div className="-mx-2 mt-1 space-y-1 whitespace-nowrap">
                  <button
                    className={classNames(
                      'text-indigo-200 hover:bg-indigo-700 hover:text-white',
                      'group flex w-full gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 transition-opacity',
                      collapsed ? 'opacity-0' : 'opacity-100',
                    )}
                    onClick={() => {
                      setShowAllTeams(true);
                    }}
                  >
                    <span className="truncate">Show all</span>
                  </button>
                </div>
              ) : (
                <></>
              )}
            </li>
            <li className="-mx-6 mt-auto">
              <button
                onClick={() => navigate('/workspaces')}
                className={classNames(
                  'flex w-full items-center gap-x-4 py-3 text-sm font-semibold leading-6 text-white hover:bg-indigo-700',
                  collapsed ? 'justify-center px-2' : 'px-6',
                )}
              >
                <img
                  className="h-8 w-8 rounded-full bg-indigo-700"
                  src={firebaseUser?.photoURL ?? ''}
                  alt=""
                />
                <span className="sr-only">Your profile</span>
                <div
                  className={classNames(
                    'flex-col items-start',
                    collapsed ? 'hidden' : 'flex',
                  )}
                >
                  <span>{firebaseUser?.displayName}</span>
                  {/* <span className="text-xs text-gray-200">
                    {firebaseUser?.email}
                  </span> */}
                </div>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
}
